import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import { contentTypeLabels } from '@/mocks/movies';

interface LatestSectionProps {
  items: Movie[];
}

export default function LatestSection({ items }: LatestSectionProps) {
  const [visible, setVisible] = useState(6);
  const shown = items.slice(0, visible);
  const hasMore = visible < items.length;

  return (
    <section className="w-full px-4 md:px-8 py-8 md:py-10 bg-background-100">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-1.5 h-7 rounded-full bg-primary-700"></span>
        <div>
          <h2 className="font-heading font-black text-xl md:text-2xl text-foreground-900">最新上线</h2>
          <p className="text-xs md:text-sm text-foreground-500 mt-0.5">每日更新的最新影视资源，中文字幕同步呈现</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
        {shown.map((movie) => (
          <Link
            key={movie.id}
            to={`/detail/${movie.id}`}
            className="group flex gap-3 md:gap-4 p-3 rounded-lg bg-background-50 border border-background-200/80 hover:border-accent-300 transition-colors cursor-pointer"
          >
            <div className="relative w-[72px] h-[100px] md:w-[84px] md:h-[116px] rounded-md overflow-hidden shrink-0 bg-primary-900">
              <img
                src={movie.cover}
                alt={`${movie.title} 封面 - 亚洲中文字幕在线观看`}
                title={`${movie.title} - 亚洲中文字幕在线观看`}
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-accent-500 text-white text-[10px] font-bold">
                {movie.rating.toFixed(1)}
              </span>
            </div>

            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-bold text-base text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                  {movie.title}
                </h3>
                <span className="shrink-0 px-2 py-0.5 rounded bg-primary-100 text-primary-800 text-[11px] whitespace-nowrap">
                  {movie.status}
                </span>
              </div>
              <p className="text-xs text-foreground-500 mt-1">
                {contentTypeLabels[movie.type]} · {movie.region} · {movie.year} · {movie.duration}
              </p>
              <p className="text-xs md:text-sm text-foreground-600 mt-2 leading-relaxed line-clamp-2 flex-1">
                {movie.summary}
              </p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-foreground-400">更新于 {movie.updateDate}</span>
                <span className="inline-flex items-center gap-1 text-xs text-accent-600">
                  <i className="ri-play-circle-line text-base"></i>
                  观看
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {hasMore ? (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + 6)}
            className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>
            加载更多
          </button>
        </div>
      ) : (
        <p className="mt-6 text-center text-xs text-foreground-400">已经到底啦，更多精彩内容持续更新中</p>
      )}
    </section>
  );
}